import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import classNames from 'classnames';
import {requireImage} from '../../../../utils/utils';
import {ObservationForm} from '../ObservationForm';
import {SimpleModal} from '../../../../components/simple-modal/component';
import {NotificationModal} from '../../../../components/notification-modal/component';
import {SurveyModal} from '../../../../modals/survey-modal/SurveyModal.component';
import {TsVideoSubscriber} from '../../../../components/ts-video-subscriber/TsVideoSubscriber';
import {TsImageEditor} from '../../../../components/ts-image-editor/ts-image-editor';
import {TsDashboardTitle} from '../../../../components/ts-dashboard-title/component';
import {Toast} from '../../../../components/toast/Toast';

import './MainMobile.scss';

export interface IMainMobileProps {
    main: any;
}

@observer
export class MainMobile extends TechseeBaseComponent<IMainMobileProps> {
    renderInternal(): JSX.Element {
        const {main} = this.props;
        const dashboardState = main?.dashboardState;

        if (!dashboardState?.dashboardControllerLoaded) {
            return <React.Fragment />;
        }

        const isVideoMode = main.isVideoOrScreensharingMode?.(dashboardState.clientRoomStorage.mode);
        const hasImages = main.MeetingMode?.images && !dashboardState.videoModeState.videoState.focus;
        const screenshots = [...(dashboardState.screenshots || [])].reverse();
        const isSubscriberVisible = isVideoMode && !dashboardState.videoModeState.videoState.isPaused;

        return (
            <div
                className={classNames('ts-dashboard-main-mobile-container', {
                    'ts-mobile-ios': main.environmentDetect?.isIOS()
                })}>
                {main.customerEndedTheMeetingModalCtrl && <SimpleModal ctrl={main.customerEndedTheMeetingModalCtrl} />}

                {main.simpleModalCtrl && <SimpleModal ctrl={main.simpleModalCtrl} />}

                {main.notificationCtrl && <NotificationModal ctrl={main.notificationCtrl} />}

                {main.surveyModalController && <SurveyModal ctrl={main.surveyModalController} />}

                <div className={classNames('ts-mobile-main-drawer', {open: dashboardState.drawerOpen})}>
                    <div className='ts-mobile-main-drawer-content'>
                        <header className='ts-mobile-main-header'>
                            <div className='text-left logo-container'>
                                <img className='logo' src={requireImage('icon-blue-techsee.png')} alt='' />
                                <div className='mode-text'>
                                    <div>{main.translate('MOBILE_MAIN.VIEW.TITLE.TECHSEE_SUPPORT')}</div>
                                    {dashboardState && main.videoModeController && (
                                        <TsDashboardTitle
                                            dashboardState={dashboardState}
                                            handleAction={main.handleDashboardTitleAction}
                                            showMuteButton={
                                                dashboardState.displayAudioButton && dashboardState.useNewInterface
                                            }
                                            toggleMute={main.videoModeController.toggleMute}
                                        />
                                    )}
                                </div>
                            </div>
                            {isVideoMode && (
                                <a
                                    className={classNames(
                                        'main-text-color ts-mobile-main-header-button ts-mobile-main-header-hint landscape-only',
                                        {'disabled-mobile-button': main.observe}
                                    )}
                                    onClick={() => main.streamAction(main.CLASSIFY_EVENT_SOURCES.VIDEO_CLICKED)}>
                                    <img
                                        className='ts-menu-icon'
                                        src={requireImage('mobile-dashboard/screenshot-blue.png')}
                                        alt=''
                                    />
                                </a>
                            )}
                        </header>

                        <div className='ts-mobile-main-content'>
                            <div className='ts-mobile-main-controls'>
                                <div className='ts-mobile-main-session-buttons-top'>
                                    {hasImages && (
                                        <div className='ts-meeting-mode-icons'>
                                            {!main.observe && main.currentResource?.url && (
                                                <div
                                                    className={classNames('ts-mobile-main-menu-button', {
                                                        open: dashboardState.mobileMenuDropdownOpen
                                                    })}>
                                                    <a
                                                        className='main-text-color'
                                                        onClick={() => main.openMobileMenuDropdownOpen()}>
                                                        <img
                                                            className='ts-menu-icon ts-annotations-dynamic-icon'
                                                            src={requireImage(
                                                                `mobile-dashboard/${dashboardState.imageEditorState.activeAnnotationTool || 'drag'}.png`
                                                            )}
                                                            alt=''
                                                        />
                                                        {!main.isActiveTool('drag') && !main.isActiveTool('text') && (
                                                            <div className='ts-annotations-color-dot' />
                                                        )}
                                                        {main.translate('MOBILE_MAIN.VIEW.TOOLBOX')}
                                                        <div
                                                            className='ts-three-dots'
                                                            style={{color: main.getActiveColorName()}}>
                                                            ...
                                                        </div>
                                                    </a>
                                                    {dashboardState.mobileMenuDropdownOpen && (
                                                        <div className='dropdown-menu'>
                                                            <h5>
                                                                {main.translate(
                                                                    'DASHBOARD_MINI.VIEW.ANNOTATION_TOOLBOX'
                                                                )}
                                                            </h5>
                                                            <div className='ts-mobile-main-annotations'>
                                                                {['pen', 'arrow', 'circle', 'rectangle', 'hand'].map(
                                                                    (tool) => (
                                                                        <a
                                                                            key={tool}
                                                                            className={classNames({
                                                                                selected: main.isActiveTool(tool)
                                                                            })}
                                                                            onClick={() => main.selectTool(tool)}>
                                                                            <img
                                                                                src={requireImage(
                                                                                    `mobile-dashboard/${tool}.png`
                                                                                )}
                                                                                alt=''
                                                                            />
                                                                        </a>
                                                                    )
                                                                )}
                                                            </div>
                                                            <h5 className='ts-color-picker-title'>
                                                                {main.translate('DASHBOARD_MINI.VIEW.TOOLBOX_COLOR')}
                                                            </h5>
                                                            <div className='ts-mobile-main-colors'>
                                                                {[9, 6, 5, 7].map((idx) => (
                                                                    <a
                                                                        key={idx}
                                                                        className={classNames({
                                                                            selected: main.isColorSelected(
                                                                                main.colors[idx].hexCode
                                                                            )
                                                                        })}
                                                                        onClick={() =>
                                                                            main.setColor(main.colors[idx].hexCode)
                                                                        }>
                                                                        <div
                                                                            className='color-block'
                                                                            style={{
                                                                                background: main.colors[idx].hexCode
                                                                            }}
                                                                        />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {!main.observe && dashboardState.currentResource?.url && (
                                                <a
                                                    className={classNames('ts-mobile-main-menu-button', {
                                                        'disabled-mobile-button': !main.hasAnnotations()
                                                    })}
                                                    onClick={() => main.undo()}>
                                                    <img
                                                        className='ts-menu-icon'
                                                        src={requireImage('mobile-dashboard/undo.png')}
                                                        alt=''
                                                    />
                                                    {main.translate('MOBILE_MAIN.VIEW.UNDO')}
                                                </a>
                                            )}
                                            {(!isVideoMode || !dashboardState.videoModeState.videoState.focus) &&
                                                dashboardState.currentResource?.url &&
                                                main.displaySendButton && (
                                                    <a
                                                        className='ts-mobile-main-menu-button ts-mobile-main-menu-send'
                                                        onClick={() => main.sendImage()}>
                                                        <img
                                                            className='ts-menu-icon'
                                                            src={requireImage('mobile-dashboard/send.png')}
                                                            alt=''
                                                        />
                                                        {main.translate('MOBILE_MAIN.VIEW.SEND')}
                                                    </a>
                                                )}
                                        </div>
                                    )}

                                    {isVideoMode && dashboardState.videoModeState.videoState.focus && !main.observe && (
                                        <div className='ts-snapshot-wrapper'>
                                            <a
                                                className={classNames(
                                                    'ts-mobile-main-menu-button main-text-color portrait-only',
                                                    {'disabled-mobile-button': main.observe}
                                                )}
                                                onClick={() =>
                                                    main.streamAction(main.CLASSIFY_EVENT_SOURCES.VIDEO_CLICKED)
                                                }>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mobile-dashboard/screenshot.png')}
                                                    alt=''
                                                />
                                                <small>{main.translate('MOBILE_MAIN.VIEW.SNAPSHOT')}</small>
                                            </a>
                                        </div>
                                    )}

                                    {main.enableObservation && !main.observe && (
                                        <div
                                            className={classNames('observation-main-button', {
                                                selected: dashboardState.displayObserverSendingForm
                                            })}>
                                            {main.observerDetails?.connected && <div className='redIndicator' />}
                                            <a
                                                className={classNames('ts-mobile-main-menu-button main-text-color', {
                                                    'ts-observer-connected white-text': main.observerDetails?.connected
                                                })}
                                                onClick={() => main.observerClick()}>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mobile-dashboard/observer.png')}
                                                    alt=''
                                                />
                                                <small className='portrait-only'>
                                                    {main.translate('MOBILE_MAIN.VIEW.OBSERVER')}
                                                </small>
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className='ts-mobile-main-session-buttons-bottom'>
                                    {dashboardState.clientRoomStorage.audioSupport &&
                                        dashboardState.clientRoomStorage.audioSupport &&
                                        main.videoModeController && (
                                            <a
                                                className='ts-mobile-main-menu-button orange-button'
                                                onClick={() => main.videoModeController.toggleMute()}>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage(
                                                        dashboardState.videoModeState.isMicrophoneMuted
                                                            ? 'mobile-dashboard/audio-off.png'
                                                            : 'mobile-dashboard/audio-on.png'
                                                    )}
                                                    alt=''
                                                />
                                                <span>{main.audioService.voipMicText}</span>
                                            </a>
                                        )}

                                    {main.addressLocation?.icon !== 'unavailable' && (
                                        <a
                                            className={classNames(
                                                `ts-mobile-main-menu-button location-${main.addressLocation?.icon} white-text`,
                                                {'ts-location-selected': dashboardState.showLocationPopover}
                                            )}
                                            onClick={() => main.triggerLocationPopover()}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mobile-dashboard/location.png')}
                                                alt=''
                                            />
                                            {main.translate('MOBILE_MAIN.VIEW.LOCATION')}
                                        </a>
                                    )}

                                    {!main.observe &&
                                        dashboardState.clientRoomStorage.mode !== main.MeetingMode.video &&
                                        main.isModeAvailable(main.MeetingMode.video) && (
                                            <a
                                                className={classNames(
                                                    'ts-mobile-main-menu-button orange-button button-overlap-group',
                                                    {'disabled-mobile-button': !dashboardState.modesEnabled}
                                                )}
                                                onClick={() =>
                                                    dashboardState.modesEnabled &&
                                                    main.requestMode(main.MeetingMode.video)
                                                }>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mobile-dashboard/switch-video.png')}
                                                    alt=''
                                                />
                                                {main.translate('MOBILE_MAIN.VIEW.VIDEO_MODE')}
                                            </a>
                                        )}

                                    {!main.observe &&
                                        isVideoMode &&
                                        (main.isModeAvailable(main.MeetingMode.oneClick) ||
                                            main.isModeAvailable(main.MeetingMode.images)) && (
                                            <a
                                                className={classNames(
                                                    'ts-mobile-main-menu-button orange-button button-overlap-group',
                                                    {'disabled-mobile-button': !dashboardState.modesEnabled}
                                                )}
                                                onClick={() =>
                                                    dashboardState.modesEnabled && main.getImageSharingType()
                                                }>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mobile-dashboard/switch-camera.png')}
                                                    alt=''
                                                />
                                                {main.translate('MOBILE_MAIN.VIEW.PHOTO_MODE')}
                                            </a>
                                        )}

                                    {!main.observe && !main.dashboardState?.offlineMode && (
                                        <a
                                            className='ts-mobile-main-menu-button'
                                            onClick={() =>
                                                main.endMeeting(false, main.LogEvents.closedByTypes.dashboard)
                                            }>
                                            <div>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mobile-dashboard/end-meeting.png')}
                                                    alt=''
                                                />
                                                <span>
                                                    {!main.offlineRoom &&
                                                    dashboardState.connectionStatus !==
                                                        main.CONNECTION_STATUS.DISCONNECTED
                                                        ? main.translate('MOBILE_MAIN.VIEW.END_MEETING')
                                                        : main.translate('DASHBOARD.VIEW.EXIT')}
                                                </span>
                                            </div>
                                        </a>
                                    )}

                                    {main.observe && !main.dashboardState?.offlineMode && (
                                        <a
                                            className='ts-mobile-main-menu-button red-button'
                                            onClick={() => main.leaveMeeting()}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mobile-dashboard/end-meeting.png')}
                                                alt=''
                                            />
                                            <span>{main.translate('DASHBOARD.VIEW.EXIT')}</span>
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className='ts-notifications-container'>
                                {dashboardState?.messageAlert?.display && (
                                    <div
                                        className='message-alert-audio use-common-styles'
                                        style={{color: dashboardState.messageAlert.color}}>
                                        {dashboardState.messageAlert.label}
                                    </div>
                                )}
                                <div className='ts-toast-container'>
                                    {dashboardState?.lastEvent?.text && (
                                        <Toast message={dashboardState.lastEvent.text} duration={2500} />
                                    )}
                                </div>
                            </div>

                            {main.enableObservation && <ObservationForm main={main} />}

                            <div className='ts-mobile-main-canvas-area'>
                                {/* Video Subscriber - kept mounted to avoid iOS duplicate video race condition */}
                                {main.tsVideoSubscriberController && (
                                    <div
                                        className={classNames('ts-video-subscriber-container', {
                                            'pointer-disabled': main.isClientPointerUnavailable
                                        })}
                                        style={isSubscriberVisible ? undefined : {display: 'none'}}>
                                        <TsVideoSubscriber
                                            controller={main.tsVideoSubscriberController}
                                            className='ts-video-subscriber-mobile'
                                            detachable={{
                                                enabled: true,
                                                condition: !dashboardState.videoModeState.videoState.focus,
                                                targetSelector: '.ts-detached-video-target',
                                                position: 'append'
                                            }}
                                        />
                                    </div>
                                )}

                                {!dashboardState.videoModeState.videoState.focus &&
                                    dashboardState.currentResource &&
                                    !main.videoControlService?.isActive &&
                                    main.imageEditorController && (
                                        <div
                                            className='ts-mobile-main-image-container'
                                            onMouseDown={() => main.closeMobileMenuDropdownOpen()}>
                                            <TsImageEditor controller={main.imageEditorController} />
                                        </div>
                                    )}

                                {dashboardState.clientRoomStorage.mode === main.MeetingMode.images &&
                                    !dashboardState.currentResource?.url && (
                                        <div className='ts-mobile-main-initial-screen'>
                                            {dashboardState.clientRoomStorage.mode === main.MeetingMode.images && (
                                                <div>
                                                    <h4>{main.translate('MOBILE_MAIN.VIEW.INSTRUCTION_ASK')}</h4>
                                                    <ol>
                                                        <li>{main.translate('MOBILE_MAIN.VIEW.INSTRUCTION_TAP')}</li>
                                                        <li>
                                                            {main.translate('MOBILE_MAIN.VIEW.INSTRUCTION_TAKE_PHOTO')}
                                                        </li>
                                                        <li>
                                                            {main.translate(
                                                                'MOBILE_MAIN.VIEW.INSTRUCTION_PROBLEM_AREA'
                                                            )}
                                                        </li>
                                                        <li>
                                                            {main.translate('MOBILE_MAIN.VIEW.INSTRUCTION_USE_PHOTO')}
                                                        </li>
                                                    </ol>
                                                </div>
                                            )}
                                            {!dashboardState.clientRoomStorage.connected && (
                                                <div>
                                                    <h6>{main.translate('DASHBOARD.VIEW.WAITING_FOR_CUSTOMER')}</h6>
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </div>

                            {dashboardState.showLocationPopover && dashboardState.mobilePopoverLocation && (
                                <div className='ts-location-popover'>
                                    <img
                                        className='ts-location-popover-icon'
                                        src={requireImage('mobile-dashboard/location.png')}
                                        alt=''
                                    />
                                    {dashboardState.mobilePopoverLocation}
                                </div>
                            )}

                            <div className='ts-mobile-main-img-strip-container'>
                                <ul className='ts-mobile-main-img-strip'>
                                    <li
                                        className='ts-detached-video-target ts-mobile-main-meeting _video'
                                        style={{
                                            display:
                                                main.isVideoOrScreensharingMode?.(
                                                    dashboardState.clientRoomStorage.mode
                                                ) && !dashboardState.videoModeState.videoState.focus
                                                    ? ''
                                                    : 'none'
                                        }}
                                        onClick={() => main.preVideoClick()}>
                                        <img
                                            src={requireImage('mobile-dashboard/video-small-white.png')}
                                            className='video-img-icon'
                                            alt=''
                                        />
                                        <div className='ts-back-to-video'>
                                            {main.translate('MOBILE_MAIN.VIEW.BACK_TO_VIDEO')}
                                        </div>
                                    </li>
                                    {screenshots.map((image, index) => (
                                        <li
                                            key={index}
                                            className={classNames('ts-mobile-main-meeting', {
                                                active: !main.video.focus && main.isActiveResource(image)
                                            })}
                                            onClick={() => main.selectClickedResource(image)}
                                            style={{backgroundImage: `url(${image.url})`}}>
                                            {screenshots.length > 0 && index === 0 && (
                                                <div className='ts-gallery-title'>
                                                    {main.translate('MOBILE_MAIN.VIEW.GALLERY')}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='audio-subscriber-container-client' />
                <div className='audio-subscriber-container-observer' />
                <div className='audio-subscriber-container-agent' />
            </div>
        );
    }
}
